/**
 * CSV upload.
 *
 * Two paths, chosen by size, matching what the server accepts:
 *
 *   under 50MB   one POST of the raw bytes to /api/import/upload
 *   50MB or over multipart — create a session, PUT each part, complete
 *
 * These are the legacy endpoints on purpose. They move opaque file content
 * rather than a JSON contract, so there is nothing for the typed v1 surface to
 * describe. The step that *does* have a contract — which campaign, which
 * columns, which validations — is POST /api/v1/imports, and that is typed.
 */
import { getApiUrl, getToken, ApiError } from './client';
import type { SheetFilter } from './queries';

/** The server's own threshold for requiring a multipart session. */
const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024;
/**
 * Part size. The part route's DefaultBodyLimit is 15MB and nginx allows 500MB
 * on this location, so 10MB leaves headroom on the binding constraint.
 */
const PART_SIZE = 10 * 1024 * 1024;

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getToken();
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getApiUrl()}${path}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(
      (json as { error?: string }).error ?? `Upload step failed (${res.status})`,
      res.status,
    );
  }
  return json as T;
}

/**
 * Read only enough of the file to get the header row and a few samples.
 *
 * Deliberately a slice, not the whole file. Reading a multi-hundred-MB CSV
 * into a string to find its first line is what made the web importer fall over
 * on large files (fixed in 194e4f3); a header is in the first few KB by
 * definition.
 */
export async function readCsvPreview(
  file: File,
): Promise<{ headers: string[]; sample: string[][]; }> {
  const slice = await file.slice(0, 256 * 1024).text();
  const lines = slice.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length === 0) return { headers: [], sample: [] };

  // Minimal RFC-4180 split: quoted fields may contain commas.
  const parse = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (quoted) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i += 1; }
        else if (ch === '"') quoted = false;
        else cur += ch;
      } else if (ch === '"') quoted = true;
      else if (ch === ',') { out.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };

  const headers = parse(lines[0] ?? '');
  // The last line of a slice is almost certainly cut mid-row, so drop it.
  const sample = lines.slice(1, 4).map(parse);
  return { headers, sample };
}

export interface UploadResult {
  fileKey: string;
}

/**
 * Upload a file and return the key to pass to POST /api/v1/imports.
 *
 * `onProgress` reports 0-100 across the whole transfer, so a 400MB multipart
 * upload shows real movement rather than sitting at zero until it finishes.
 */
export async function uploadCsv(
  file: File,
  campaignId: string,
  onProgress: (pct: number) => void,
): Promise<UploadResult> {
  const { fileKey } = await post<{ fileKey: string }>('/api/import/get-upload-url', {
    fileName: file.name,
    campaignId,
  });

  if (file.size < LARGE_FILE_THRESHOLD) {
    onProgress(5);
    const res = await fetch(`${getApiUrl()}/api/import/upload`, {
      method: 'POST',
      headers: authHeaders({
        'Content-Type': 'application/octet-stream',
        'x-file-name': file.name,
      }),
      body: file,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new ApiError(
        (err as { error?: string }).error ?? `Upload failed (${res.status})`,
        res.status,
      );
    }
    const json = (await res.json().catch(() => ({}))) as { fileKey?: string };
    onProgress(100);
    return { fileKey: json.fileKey ?? fileKey };
  }

  /*
    The session returns a token as well as an id, and every later call has to
    carry it: the part upload as an `x-upload-token` header, complete and abort
    in the body. The server checks it against `multipart_uploads` and refuses
    the request without it, so dropping the token fails the whole upload.
  */
  const { uploadId, uploadToken } = await post<{ uploadId: string; uploadToken: string }>(
    '/api/import/multipart/create',
    { fileKey, campaignId },
  );

  const partCount = Math.ceil(file.size / PART_SIZE);
  const parts: { partNumber: number; etag: string }[] = [];

  for (let i = 0; i < partCount; i += 1) {
    const chunk = file.slice(i * PART_SIZE, (i + 1) * PART_SIZE);
    const q = new URLSearchParams({
      uploadId,
      partNumber: String(i + 1),
      fileKey,
    });
    const res = await fetch(`${getApiUrl()}/api/import/multipart/part?${q}`, {
      method: 'PUT',
      headers: authHeaders({
        'Content-Type': 'application/octet-stream',
        'x-upload-token': uploadToken,
      }),
      body: chunk,
    });
    if (!res.ok) {
      // Abandon the session so a failed upload does not leave part files
      // behind on the server for nobody to clean up.
      await post('/api/import/multipart/abort', {
        uploadId,
        fileKey,
        uploadToken,
      }).catch(() => {});
      throw new ApiError(`Part ${i + 1} of ${partCount} failed (${res.status})`, res.status);
    }
    const json = (await res.json().catch(() => ({}))) as { etag?: string };
    parts.push({ partNumber: i + 1, etag: json.etag ?? '' });
    onProgress(Math.round(((i + 1) / partCount) * 100));
  }

  await post('/api/import/multipart/complete', { uploadId, fileKey, uploadToken, parts });
  return { fileKey };
}

/**
 * Upload a CSV for HLR phone verification.
 *
 * Legacy endpoint on purpose, same reasoning as `uploadCsv`: this moves opaque
 * file bytes, and the file name and phone column ride as headers rather than
 * as a JSON body. The parts with a real contract — job status and history —
 * are the typed `/api/v1/verify` endpoints.
 *
 * `phoneColumn` overrides the server's auto-detection, which only recognises a
 * known set of header names and picks the wrong column on unusual files.
 */
export async function uploadVerifyCsv(
  file: File,
  phoneColumn: string | null,
): Promise<{ verifyId: string }> {
  const res = await fetch(`${getApiUrl()}/api/verify/upload`, {
    method: 'POST',
    headers: authHeaders({
      'Content-Type': 'application/octet-stream',
      'x-file-name': file.name,
      ...(phoneColumn ? { 'x-phone-column': phoneColumn } : {}),
    }),
    body: file,
  });
  const json = (await res.json().catch(() => ({}))) as { verifyId?: string; error?: string };
  if (!res.ok || !json.verifyId) {
    throw new ApiError(json.error ?? `Verification upload failed (${res.status})`, res.status);
  }
  return { verifyId: json.verifyId };
}

/**
 * Download a finished verification's results.
 *
 * A plain link cannot carry the bearer token, so the file is fetched and
 * handed to the browser as a blob. `clean` returns only the numbers HLR
 * confirmed; without it you get every row with its verdict attached.
 */
export async function downloadVerifyResult(jobId: string, clean: boolean): Promise<void> {
  const res = await fetch(
    `${getApiUrl()}/api/verify/${encodeURIComponent(jobId)}/download?clean=${clean}`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    throw new ApiError(`Download failed (${res.status})`, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${clean ? 'verified' : 'results'}-${jobId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Export the rows matching a filter set as CSV, as rows-YYYY-MM-DD.csv.
 *
 * Posts the identical body the leads table searches with, so the file cannot
 * contain different rows from what was on screen. Not part of the generated
 * client because the response is a file rather than a JSON contract; the
 * request shape is still the typed one.
 */
export async function exportLeads(filters: unknown): Promise<number> {
  const res = await fetch(`${getApiUrl()}/api/v1/leads/export`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(filters),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
    throw new ApiError(j.detail ?? j.error ?? `Export failed (${res.status})`, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Named in the app's words (rows-2026-10-18.csv); the columns inside keep
  // their keys.
  a.download = `rows-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return blob.size;
}

/**
 * Upload a CSV purely to look at it.
 *
 * Reuses the import upload path — same single-shot and multipart handling,
 * same size threshold — because the bytes move identically; only what happens
 * afterwards differs. The campaign is empty because there is not one: the
 * upload endpoints accept the field and never read it, and nothing is imported
 * until someone asks for it on the import screen.
 */
export async function uploadSheetCsv(
  file: File,
  onProgress: (pct: number) => void,
): Promise<UploadResult> {
  return uploadCsv(file, '', onProgress);
}

/**
 * Download a sheet as CSV, with every change applied.
 *
 * A plain link cannot carry the bearer token, so the file is fetched and
 * handed to the browser as a blob — the same approach as the verify download.
 * The server streams it, so the browser holds the bytes but this process
 * never assembles them server-side.
 */
export async function downloadSheet(
  id: string,
  fileName: string,
  version?: number,
  /** A block of rows, for splitting a file between buyers. */
  range?: { from: number; to: number },
  /*
    The view's filters, so an export is what was on screen.

    A range is counted within them, which is what makes splitting a filtered
    file mean anything: block two is the second block of the rows that
    matched, not of the file they came from.
  */
  filters?: SheetFilter[],
): Promise<void> {
  const parts = [
    ...(version ? [`version=${version}`] : []),
    ...(range ? [`from=${range.from}`, `to=${range.to}`] : []),
    ...(filters && filters.length
      ? [`filters=${encodeURIComponent(JSON.stringify(filters))}`]
      : []),
  ];
  const q = parts.length ? `?${parts.join('&')}` : '';
  const res = await fetch(`${getApiUrl()}/api/v1/sheets/${encodeURIComponent(id)}/export${q}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(
      (err as { detail?: string; error?: string }).detail
        ?? (err as { error?: string }).error
        ?? `Download failed (${res.status})`,
      res.status,
    );
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  // The version is in the name, or two downloads of the same file are
  // indistinguishable in a downloads folder.
  a.download =
    fileName.replace(/\.[^.]*$/, '') +
    (version ? `.v${version}` : '') +
    (range ? `.rows${range.from}-${range.to}` : '') +
    '.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Save a statement's per-lead CSV: a stored one by id, or a draft by its
 * kind, party and month.
 */
export async function downloadStatementCsv(
  target: { id: string } | { kind: 'buyer' | 'supplier'; partyId: string; period: string },
  filename: string,
): Promise<void> {
  const path = 'id' in target
    ? `/api/v1/statements/${encodeURIComponent(target.id)}/csv`
    : `/api/v1/statements/draft/csv?kind=${target.kind}&party_id=${encodeURIComponent(target.partyId)}&period=${target.period}`;
  const res = await fetch(`${getApiUrl()}${path}`, { headers: authHeaders() });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
    throw new ApiError(j.detail ?? j.error ?? `Download failed (${res.status})`, res.status);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
